/**
 * advance-surface-batteries.ts — per-tick Surface Battery offensive
 * pass (v2M6.5, STRUCTURES §2.2).
 *
 * Each tick: for every Energy / Kinetic Surface Battery on every owned
 * planet, if the battery has finished construction (level ≥ 1) and its
 * cooldown is 0, pick the best researched weapon in the battery's
 * category, find the closest hostile ship in the same system within
 * weapon range, and fire one volley at it. Damage flows through the
 * exact same shield → armor → hull chain ship-vs-ship combat uses
 * (`applyVolleyToShip` from advance-combat.ts).
 *
 * Pipeline order: runs after `advanceCombat` so it doesn't fight ship
 * cooldowns (separate state — battery cooldowns live on the structure)
 * and BEFORE `advanceBombardment` so a defender's batteries can kill
 * an attacker before it gets a bombardment shot off this tick.
 *
 * v2M6.5 simplifications (revisit in v2M7 balance):
 *  - No team-vision gate. Any in-range hostile is a valid target.
 *  - Single-shot per fire (count = 1). A battery is one mount.
 *  - Targeting: closest hostile, ship-id ascending tiebreak.
 *  - Range: weapon range × state.balanceData.combat.weaponRangeScale + planet sim radius
 *    (mirrors bombardment range-to-edge, just inverted perspective).
 */

import { applyVolleyToShip } from './advance-combat';
import { cooldownTicksFor } from './combat-balance';
import { planetSimRadiusForSize } from './balance-bombardment';
import {
  EMPIRE_NEUTRAL_ID,
  type EmpireId,
  type GameState,
  type Planet,
  type ShipInstance,
  type StructureKind,
  type WeaponDef,
} from '../state/types/index';

/** Map a Surface Battery kind to its weapon category. */
function categoryForBattery(
  kind: StructureKind,
): 'Energy' | 'Kinetic' | null {
  if (kind === 'energy-surface-battery') return 'Energy';
  if (kind === 'kinetic-surface-battery') return 'Kinetic';
  return null;
}

/** Advance Surface Batteries by exactly one tick. Mutates state. */
export function advanceSurfaceBatteries(state: GameState): void {
  // Iterate planets in id order for deterministic dispatch. Per-planet,
  // structures are iterated in their list order — that's also stable
  // (insertion order, mutated only by build/destroy).
  const planetIds = [...state.planets.keys()].sort((a, b) => a - b);
  for (const pid of planetIds) {
    const planet = state.planets.get(pid);
    if (!planet) continue;
    if (planet.ownerEmpireId === null) continue; // uninhabited planets don't shoot
    const ownerId = planet.ownerEmpireId;
    const owner = state.empires.get(ownerId);
    if (!owner) continue;

    for (const sid of planet.structureIds) {
      const s = state.structures.get(sid);
      if (!s) continue;
      const category = categoryForBattery(s.kind);
      if (category === null) continue;
      // Tick cooldown down regardless of whether we end up firing — same
      // pattern as combat. Skip the firing branch while still cooling.
      if (s.weaponCooldownTicks > 0) {
        s.weaponCooldownTicks -= 1;
        continue;
      }
      if (s.level < 1) continue; // under construction, not yet operational
      if (s.currentHull <= 0) continue; // defensive — destroyed structures should be gone

      const weapon = bestWeaponInCategory(state, owner, category);
      if (!weapon) continue;
      // EMP weapons in the catalogue would currently no-op against ships
      // (per MECHANICS — EMP disables, doesn't damage HP). v1 has no EMP
      // entries in Energy or Kinetic categories so this is forward-compat
      // defensive: skip them if they ever appear.
      if (weapon.damageType === 'EMP') continue;

      const range = weapon.range * state.balanceData.combat.weaponRangeScale + planetSimRadiusForSize(planet.size);
      const target = pickShipTargetForBattery(state, planet, ownerId, range);
      if (!target) continue;

      // MFix5.3 — Surface Batteries are now unique-per-planet; firepower
      // scales by level (one L_N battery = the volume of N L1 batteries
      // before, preserving the v2M6.5 firepower ceiling via upgrade
      // rather than stacking).
      // v3M19 — pass ownerId as `attackerEmpireId` so the emitted
      // DamageEvent records the firing planet's empire (for the future
      // "always show damage I dealt" preview override; not load-bearing
      // for the v3M19 FoW gate which keys on the target).
      applyVolleyToShip(state, target, s.level, weapon, 1, ownerId);
      s.weaponCooldownTicks = cooldownTicksFor(weapon.rateOfFire);
    }
  }

  // Cull dead ships. Mirrors advance-combat's end-of-pass despawn so the
  // pipeline stays clean for any pass that iterates ships after this one
  // (advance-bombardment runs next). Inlined rather than calling
  // despawnShip from advance-combat.ts to keep this module dependency-
  // free of advance-combat's internals — applyVolleyToShip is the only
  // shared seam.
  const dead: number[] = [];
  for (const ship of state.ships.values()) {
    if (ship.currentHull <= 0) dead.push(ship.id);
  }
  if (dead.length === 0) return;
  dead.sort((a, b) => a - b);
  for (const id of dead) {
    const ship = state.ships.get(id);
    if (!ship) continue;
    state.ships.delete(id);
    const empire = state.empires.get(ship.ownerEmpireId);
    if (empire) {
      const idx = empire.shipIds.indexOf(id);
      if (idx >= 0) empire.shipIds.splice(idx, 1);
    }
    // Scrub from every team's vision map so detection doesn't keep a
    // ghost record of the destroyed ship.
    for (const inner of state.teamVision.values()) {
      inner.delete(id);
    }
  }
}

/**
 * Pick the closest hostile ship in `planet.systemId` within `range` of
 * the planet centre. Hostility = ship's owner ≠ planet's owner. Mid-jump
 * ships (`systemId === null`) and dead ships are skipped. Tiebreak by
 * ascending ship id for determinism.
 *
 * MFix10.1 — neutral-owned planets follow a stricter rule per
 * `DIPLOMACY.md §7` ("Surface Batteries fire on the bombarding ships").
 * Neutrals fire ONLY on `planet.lastBombardingEmpireId`'s ships, and
 * only while that field is non-null. Two bugs this fixes:
 *
 *   1. A neutral with hostile ships incidentally in range (e.g. a
 *      passing patrol that isn't attacking) used to open fire on
 *      them. Now only retaliates against an actual bomber.
 *   2. While `Planet.vassalOf === playerId` the planet is still owned
 *      by `EMPIRE_NEUTRAL_ID` — so the existing "owner ≠ ship owner"
 *      hostility test fired on suzerain ships passing through. Now
 *      the suzerain is only fired on if they're literally bombarding
 *      their own vassal (which they shouldn't be).
 *
 * Owned (non-neutral) planets keep the v2M6.5 "any in-range hostile"
 * rule unchanged — that's a player-empire defence pattern, not the
 * diplomacy-neutral case.
 *
 * Returns null when no eligible target exists.
 */
function pickShipTargetForBattery(
  state: GameState,
  planet: Planet,
  ownerId: EmpireId,
  range: number,
): ShipInstance | null {
  // MFix10.1 — neutral-only narrowing of the hostile-empire set.
  // `lastBombardingEmpireId` clears as soon as bombardment ceases
  // (see advance-bombardment.ts), giving the right "stop firing once
  // the threat ends" behaviour for free. Owned planets unchanged.
  const isNeutral = planet.ownerEmpireId === EMPIRE_NEUTRAL_ID;
  const allowedAttacker = isNeutral ? planet.lastBombardingEmpireId : null;
  if (isNeutral && allowedAttacker === null) return null;

  let best: ShipInstance | null = null;
  let bestDistSq = range * range;
  for (const ship of state.ships.values()) {
    if (ship.ownerEmpireId === ownerId) continue;
    if (isNeutral && ship.ownerEmpireId !== allowedAttacker) continue;
    if (ship.systemId !== planet.systemId) continue;
    if (ship.currentHull <= 0) continue;
    const dx = ship.sx - planet.systemX;
    const dy = ship.sy - planet.systemY;
    const distSq = dx * dx + dy * dy;
    if (distSq > bestDistSq) continue;
    if (best === null || distSq < bestDistSq) {
      best = ship;
      bestDistSq = distSq;
    } else if (distSq === bestDistSq && ship.id < best.id) {
      // Tiebreak — same distance, prefer lower id for determinism.
      best = ship;
    }
  }
  return best;
}

/**
 * Find the highest-Mk weapon in `category` the empire has researched.
 * Mk 1 is always available (researchCost 0, no prereqs — see
 * `collectBaselineTechs` in scenario.ts). Per MECHANICS §3, weapons are
 * loaded from ships.json and resolved to Mk via the empire's
 * `researchedTechs`.
 *
 * Currently iterates the full weapons map each call. v2M7 may pre-compute
 * a per-empire cache if the cost shows up in profiling — but with ≤ a
 * dozen weapons in the category this is fast enough.
 */
function bestWeaponInCategory(
  state: GameState,
  empire: { researchedTechs: ReadonlySet<string> },
  category: 'Energy' | 'Kinetic',
): WeaponDef | null {
  let best: WeaponDef | null = null;
  for (const w of state.weapons.values()) {
    if (w.category !== category) continue;
    // The weapon's tech is its name — the M12 research path uses tech
    // names matching the Mk variants. If a non-Mk-1 weapon hasn't been
    // researched, skip it (Mk 1 has researchCost=0 so it always passes).
    if (!isWeaponAvailable(state, empire.researchedTechs, w)) continue;
    if (best === null || w.mk > best.mk) best = w;
  }
  return best;
}

/**
 * Whether the empire can use this weapon — Mk 1 is baseline (always
 * available); higher Mks require the matching researched tech. Mirrors
 * the implicit gating in `resolveSlotWeapon` from combat-balance.ts.
 */
function isWeaponAvailable(
  state: GameState,
  researched: ReadonlySet<string>,
  weapon: WeaponDef,
): boolean {
  if (weapon.mk <= 1) return true;
  // Tech name matches weapon name in our v1 data (M12 lock).
  return researched.has(weapon.name) || techMatchesByName(state, researched, weapon.name);
}

/** Defensive name match — also accept the catalogue tech if it differs from the weapon name. */
function techMatchesByName(
  state: GameState,
  researched: ReadonlySet<string>,
  name: string,
): boolean {
  // No-op for v1 (weapon name === tech name); placeholder for v3 if the
  // mapping diverges.
  void state;
  return researched.has(name);
}
